   import React, { useEffect, useState } from 'react';
import { 
  ShieldCheck, 
  User, 
  CreditCard, 
  Calendar, 
  Phone, 
  Mail, 
  ArrowRight, 
  Sparkles, 
  FileText, 
  TrendingUp,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { formatCreditReport } from '../../../utils/formatTransunionReport';
import TransunionHybridModal from './ReportModal';
function SummaryScore({responseData}) {
  const [showReportModal, setShowReportModal] = useState(false)

const cleanData = formatCreditReport(responseData)


// useEffect(() => {
//   console.log("clean data", cleanData);

// }, [cleanData])

// Insert this inside your component or render tree

  // Extract data with safe fallbacks
  const data = cleanData || {};
  const score = data?.score?.value || data.score || "N/A"; // CIBIL score range: 300 - 900
  const name = data?.personal?.name || data.name || "N/A";
  const pan = data?.personal?.pan || data?.pan || "N/A";
  const phone = data?.personal?.phone || data?.phone || "N/A";
  const email = data?.personal?.email || data?.email || "N/A";
  const reportDate = data?.reportDate || "N/A";
  const activeAccounts = data?.activeAccountsCount ?? "N/A";
  const totalEnquiries = data?.totalEnquiries ?? "N/A";

  // CIBIL Score gauge calculations (300 to 900 range)
  const minScore = 300;
  const maxScore = 900;
  const clampedScore = Math.min(Math.max(score, minScore), maxScore);
  const percentage = ((clampedScore - minScore) / (maxScore - minScore)) * 100;
  
  // Arc length calculations for SVG (r = 80, Arc circumference = Math.PI * 80 ≈ 251.32)
  const arcLength = 251.32;
  const strokeOffset = arcLength - (percentage / 100) * arcLength;

  // Indicator Dot Position on Arc (180 deg to 0 deg)
  const angleInRad = Math.PI - (percentage / 100) * Math.PI;
  const dotX = 100 + 80 * Math.cos(angleInRad);
  const dotY = 100 - 80 * Math.sin(angleInRad);

  // Score health badge helper
  const getScoreMeta = (val) => {
    if (val >= 750) return { label: 'Excellent', text: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle2 };
    if (val >= 700) return { label: 'Good', text: 'text-teal-600', bg: 'bg-teal-50 border-teal-200', icon: TrendingUp };
    if (val >= 650) return { label: 'Fair', text: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', icon: AlertCircle };
    return { label: 'Needs Attention', text: 'text-rose-600', bg: 'bg-rose-50 border-rose-200', icon: AlertCircle };
  };

  const meta = getScoreMeta(score);
  const StatusIcon = meta.icon;

  return (
    <div className="w-full max-w-5xl mx-auto bg-white rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/50 overflow-hidden font-sans text-slate-800">
      
      {/* Top Banner Header */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 px-6 py-4 text-white flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/10 backdrop-blur-md rounded-xl border border-white/10">
            <ShieldCheck className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="font-semibold text-base leading-tight flex items-center gap-2">
              TransUnion CIBIL <span className="text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-full font-normal">Verified</span>
            </h3>
            <p className="text-xs text-slate-400">Official Credit Health Report</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 text-xs text-slate-300 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">
          <Calendar className="w-3.5 h-3.5 text-slate-400" />
          <span>Report Date: <strong className="text-white font-medium">{reportDate}</strong></span>
        </div>
      </div>

      {/* Main Body */}
      <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        
        {/* Left Column: Modern Gauge */}
        <div className="md:col-span-5 flex flex-col items-center justify-center p-6 bg-slate-50/70 border border-slate-100 rounded-2xl relative">
          
          <div className="relative w-56 h-32 flex items-center justify-center">
            <svg viewBox="0 0 200 115" className="w-full h-full overflow-visible">
              <defs>
                <linearGradient id="cibilGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#ef4444" />
                  <stop offset="35%" stopColor="#f59e0b" />
                  <stop offset="70%" stopColor="#10b981" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>

              {/* Background Gauge Arc */}
              <path
                d="M 20 100 A 80 80 0 0 1 180 100"
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="16"
                strokeLinecap="round"
              />

              {/* Dynamic Score Arc */}
              <path
                d="M 20 100 A 80 80 0 0 1 180 100"
                fill="none"
                stroke="url(#cibilGradient)"
                strokeWidth="16"
                strokeLinecap="round"
                strokeDasharray="251.32"
                strokeDashoffset={strokeOffset}
                className="transition-all duration-1000 ease-out"
              />

              {/* Score Marker Dot */}
              <circle
                cx={dotX}
                cy={dotY}
                r="7"
                fill="#ffffff"
                stroke="#0f172a"
                strokeWidth="3.5"
                className="shadow-md transition-all duration-1000 ease-out"
              />
            </svg>

            {/* Score Text Overlay inside Gauge */}
            <div className="absolute bottom-0 text-center flex flex-col items-center">
              <span className="text-4xl font-extrabold tracking-tight text-slate-900 leading-none">
                {score}
              </span>
              <span className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase mt-1">
                Out of 900
              </span>
            </div>
          </div>

          {/* Scale Labels */}
          <div className="w-full flex justify-between text-[11px] font-semibold text-slate-400 px-3 mt-1">
            <span>300</span>
            <span>900</span>
          </div>

          {/* Status Badge */}
          <div className={`mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${meta.bg} ${meta.text}`}>
            <StatusIcon className="w-3.5 h-3.5" />
            <span>{meta.label}</span>
          </div>
        </div>

        {/* Right Column: Personal Information & Highlights */}
        <div className="md:col-span-7 flex flex-col justify-between space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <User className="w-4 h-4 text-indigo-600" />
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Personal Details</h4>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Name */}
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                <span className="text-[11px] text-slate-400 block font-medium">Full Name</span>
                <span className="text-sm font-semibold text-slate-800">{name}</span>
              </div>

              {/* PAN */}
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                <span className="text-[11px] text-slate-400 block font-medium">PAN Number</span>
                <span className="text-sm font-semibold text-slate-800 tracking-wide">{pan}</span>
              </div>

              {/* Phone */}
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                <span className="text-[11px] text-slate-400 block font-medium flex items-center gap-1">
                  <Phone className="w-3 h-3 text-slate-400" /> Phone
                </span>
                <span className="text-sm font-semibold text-slate-800">{phone}</span>
              </div>

              {/* Email */}
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                <span className="text-[11px] text-slate-400 block font-medium flex items-center gap-1">
                  <Mail className="w-3 h-3 text-slate-400" /> Email
                </span>
                <span className="text-sm font-semibold text-slate-800 truncate block">{email}</span>
              </div>
            </div>
          </div>

          {/* Report Highlights (Mini-stats) */}
          <div className="pt-2 border-t border-slate-100 grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
                <CreditCard className="w-4 h-4" />
              </div>
              <div>
                <span className="text-xs text-slate-400 block">Active Accounts</span>
                <span className="text-sm font-bold text-slate-800">{activeAccounts} Loan / Credit Cards</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <span className="text-xs text-slate-400 block">Total Enquiries</span>
                <span className="text-sm font-bold text-slate-800">{totalEnquiries} Enquiries</span>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Footer / Action Area */}
      <div className="bg-slate-50/80 px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-slate-500 flex items-center gap-1.5 text-center sm:text-left">
          <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          Full report includes payment history, detailed account breakdown, and hard inquiries.
        </p>

        <button 
          onClick={() => {
            setShowReportModal(true)
            console.log("Viewing full credit report...");
          }}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-xl shadow-lg shadow-slate-900/10 hover:shadow-slate-900/20 active:scale-[0.98] transition-all cursor-pointer group shrink-0"
        >
          <span>View Full Credit Report</span>
          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-0.5 group-hover:text-white transition-all" />
        </button>
      </div>

      {showReportModal && 
      <TransunionHybridModal report={cleanData} onClose={() => setShowReportModal(false)}/>
      }

    </div>
  );
  
}

export default SummaryScore